import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  TrendingUp, TrendingDown, Trophy, Snowflake, Flame, Target,
  Clock, ArrowUp, ArrowDown, Activity, BarChart3, Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const fmtPct = (n) => `${n.toFixed ? n.toFixed(1) : n}%`;

const KPI = ({ label, value, sub, icon: Icon, accent = 'emerald', trend }) => {
  const accents = {
    emerald: 'from-emerald-500/10 to-teal-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30',
    rose: 'from-rose-500/10 to-orange-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30',
    amber: 'from-amber-500/10 to-yellow-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
    blue: 'from-blue-500/10 to-cyan-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30',
    slate: 'from-slate-500/10 to-slate-400/10 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    violet: 'from-violet-500/10 to-purple-500/10 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30',
  };
  return (
    <div className={cn('rounded-xl p-4 bg-gradient-to-br border', accents[accent])}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium opacity-80 truncate">{label}</p>
          <p className="text-2xl sm:text-3xl font-bold mt-1 text-slate-900 dark:text-white">{value}</p>
          {sub && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sub}</p>}
        </div>
        {Icon && <Icon className="w-5 h-5 flex-shrink-0 opacity-70" />}
      </div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-2 text-xs font-medium">
          {trend > 0 ? <ArrowUp className="w-3 h-3 text-emerald-500" /> : trend < 0 ? <ArrowDown className="w-3 h-3 text-rose-500" /> : null}
          <span className={cn(trend > 0 ? 'text-emerald-600 dark:text-emerald-400' : trend < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400')}>
            {trend > 0 ? '+' : ''}{trend}% vs last week
          </span>
        </div>
      )}
    </div>
  );
};

const SourceBar = ({ source, count, total, isDark }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-700 dark:text-slate-300 font-medium truncate">{source}</span>
        <span className="text-slate-500 dark:text-slate-400">{count} ({pct}%)</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
      </div>
    </div>
  );
};

const SparklineBars = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-xs text-slate-400 dark:text-slate-500 italic py-4 text-center">No wins in the last 30 days</p>;
  }
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5 group" title={`${d.day}: ${d.count} won`}>
          <div
            className="w-full rounded-t bg-gradient-to-t from-emerald-500 to-emerald-300 dark:from-emerald-600 dark:to-emerald-400 transition-all group-hover:opacity-80"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: '4px' }}
          ></div>
        </div>
      ))}
    </div>
  );
};

/**
 * NurtureKpiDashboard
 * Displays CRM-style KPIs: leads added, conversion %, avg time-to-contact, dormant leads,
 * by-source breakdown, and a 30-day won trend sparkline.
 */
export default function NurtureKpiDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${backendUrl}/api/nurture/kpis`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('Failed');
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!data) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
        Couldn't load KPIs. Try refreshing.
      </p>
    );
  }

  const trendThisWeek = (() => {
    const cur = data.leads_added_this_week || 0;
    const prev = data.leads_added_last_week || 0;
    if (prev === 0) return cur > 0 ? 100 : 0;
    return Math.round(((cur - prev) / prev) * 100);
  })();

  const totalSourceCount = (data.by_source || []).reduce((s, x) => s + x.count, 0);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="nurture-kpi-dashboard">
      {/* Top row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KPI
          label="Leads added (this week)"
          value={data.leads_added_this_week}
          sub={`${data.leads_added_last_week} last week`}
          icon={TrendingUp}
          accent="blue"
          trend={trendThisWeek}
        />
        <KPI
          label="Hot leads"
          value={data.hot_leads_count}
          sub="Active pipeline"
          icon={Flame}
          accent="rose"
        />
        <KPI
          label="Conversion (90d)"
          value={fmtPct(data.conversion_rate)}
          sub={`${data.won_this_month} won this month`}
          icon={Trophy}
          accent="emerald"
        />
        <KPI
          label="Avg time-to-contact"
          value={`${data.avg_time_to_first_contact_hours}h`}
          sub="From created → first call"
          icon={Clock}
          accent="violet"
        />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        <KPI
          label="Won this month"
          value={data.won_this_month}
          icon={TrendingUp}
          accent="emerald"
        />
        <KPI
          label="Lost this month"
          value={data.lost_this_month}
          icon={TrendingDown}
          accent="slate"
        />
        <KPI
          label="Dormant (>14 days)"
          value={data.dormant_leads}
          sub="Leads needing a poke"
          icon={Snowflake}
          accent="amber"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 dark:text-white">
              <BarChart3 className="w-4 h-4 text-emerald-500" /> Leads by source
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.by_source || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic py-4 text-center">No leads yet</p>
            ) : (
              data.by_source.slice(0, 6).map(s => (
                <SourceBar key={s.source} source={s.source} count={s.count} total={totalSourceCount} />
              ))
            )}
          </CardContent>
        </Card>

        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 dark:text-white">
              <Activity className="w-4 h-4 text-emerald-500" /> Wins (last 30 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SparklineBars data={data.won_trend_30d} />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
              <Target className="w-3 h-3 inline mr-1" />
              {(data.won_trend_30d || []).reduce((s, x) => s + x.count, 0)} wins in the last 30 days
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
